import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { CopilotPreview, CreateDraftInput, JobEvaluationPreview, JobValuationSalaryTypeLocal, PayEquityAuditDraft, PayEquityAuditDraftV2, PublishDraftInput, UpdatePayEquityAuditDraftV2, UsersWithAccess} from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit';
import { publishDraftInputValidator, updatePayEquityAuditDraftV2Validator } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit';
import type { QueryOptions, UseMutationOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';
import { i18n } from '@/i18n';

const payEquityAuditService = new RestManager(`https://${getApiHost()}/pay-equity-audit-service`);

export type DeleteNodeResult = { id: string, reason: 'used' | 'call' };

type PartialPublishDraftInput = Omit<PublishDraftInput, 'jobValuationSalaryTypeLocal'>;

export type PublishDraftPayload = { id?: string | null, input: PartialPublishDraftInput };
export type UpdateDraftPayload = { id?: string | null, params: Partial<UpdatePayEquityAuditDraftV2> };

type PayEquityAuditDraftOptions = {
    queries: {
        getDraft: (id: string | null | undefined, enabled?: boolean, subscribe?: boolean) => QueryOptions<PayEquityAuditDraftV2 | null, Error>;
        getUsersThatWillHaveAccessState: (id: string | null | undefined, enabled?: boolean) => QueryOptions<UsersWithAccess | null, Error>;
        getJobValuationPreview: (id: string | null | undefined, enabled?: boolean) => QueryOptions<JobEvaluationPreview | null, Error>;
        getCopilotPreview: (id: string | null | undefined, enabled?: boolean) => QueryOptions<CopilotPreview | null, Error>;
    },
    mutations: {
        update: UseMutationOptions<void, Error, UpdateDraftPayload>,
        publishDraft: UseMutationOptions<string | null, Error, PublishDraftPayload>,
        createDraft: UseMutationOptions<PayEquityAuditDraft, Error, CreateDraftInput>,
        deleteDrafts: UseMutationOptions<void, Error, string[]>,
    },
};

function createLocalisation(): JobValuationSalaryTypeLocal {
    return {
        hourly: i18n.global.t('salaryTypes.2'),
        monthly: i18n.global.t('salaryTypes.1'),
        yearly: i18n.global.t('salaryTypes.0'),
        hourlyTotalCompensation: i18n.global.t('salaryTypes.5'),
        monthlyTotalCompensation: i18n.global.t('salaryTypes.4'),
        yearlyTotalCompensation: i18n.global.t('salaryTypes.3')
    };
}

export const payEquityAuditDraft: PayEquityAuditDraftOptions = {
    queries: {
        getDraft: (id?: string | null, enabled: boolean = true, subscribe: boolean = true): QueryOptions<PayEquityAuditDraftV2 | null, Error> => {
            const { instanceTopic, userData, role } = useLoginStore();
             const userId = userData?.loginUser?.Id.toLowerCase();
            const isEnabled = enabled && id != null && id !== '';
            return queryOptions<PayEquityAuditDraftV2 | null, Error>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: `v2.${instanceTopic}.${userId}.${role?.role}.pay-equity-audit-draft.${id}`,
                },
                queryKey: ['payEquityAuditDraft', instanceTopic, id, userId, role?.role],
                queryFn: async (): Promise<PayEquityAuditDraftV2 | null> => {
                    if (instanceTopic == null) {
                        throw new Error('Invalid subdomain');
                    }

                    if (userId == null) {
                        throw new Error('Invalid user');
                    }

                    if (role?.role == null) {
                        throw new Error('Invalid role');
                    }

                    if (id == null || id === '') {
                        throw new Error('Draft ID is required');
                    }

                    return ResgateManager.model<PayEquityAuditDraftV2>(`v2.${instanceTopic}.${userId}.${role.role}.pay-equity-audit-draft.${id}`).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: () => isEnabled,
            });
        },
        getUsersThatWillHaveAccessState: (id?: string | null, enabled: boolean = false): QueryOptions<UsersWithAccess | null, Error> => {
            const { instanceTopic, userId, role } = useLoginStore();
            return queryOptions<UsersWithAccess | null, Error>({
                queryKey: ['usersWithAccess', instanceTopic, id, userId, role?.role],
                queryFn: async (): Promise<UsersWithAccess | null> => {
                    if (id == null || id === '') throw new Error('Draft ID is required');
                    const result = payEquityAuditService.get<UsersWithAccess>(`v1/${instanceTopic}/${userId}/${role?.role}/pay-equity-audit-draft/${id}/users-with-access`);
                    return result;
                },
                enabled: () => enabled
            });
        },
        getJobValuationPreview: (id?: string | null, enabled: boolean = false): QueryOptions<JobEvaluationPreview | null, Error> => {
            const { instance, instanceTopic, userId, role } = useLoginStore();
            return queryOptions<JobEvaluationPreview | null, Error>({
                queryKey: ['jobValuationPreview', instance, instanceTopic, id, userId, role?.role],
                queryFn: async (): Promise<JobEvaluationPreview | null> => {
                    if (id == null || id === '') throw new Error('Draft ID is required');
                    const payload = {
                        draftId: id,
                        salaryTypeLocal: createLocalisation()
                    };

                    try {
                        return await payEquityAuditService.post<JobEvaluationPreview | null>(payload, `v2/${instance}/pay-equity-audit/job-evaluation-preview`, { 'x-subdomain': instanceTopic ?? '' });
                    } catch {
                        throw new Error('Failed to fetch job valuation preview.');
                    }

                },
                enabled: () => enabled
            });
        },
        getCopilotPreview: (id?: string | null, enabled: boolean = false): QueryOptions<CopilotPreview | null, Error> => {
            const { instance, instanceTopic, userId, role } = useLoginStore();
            return queryOptions<CopilotPreview | null, Error>({
                queryKey: ['copilotPreview', instance, instanceTopic, id, userId, role?.role],
                queryFn: async (): Promise<CopilotPreview | null> => {
                    if (id == null || id === '') throw new Error('Draft ID is required');
                     const payload = {
                        draftId: id,
                        salaryTypeLocal: createLocalisation()
                    };

                    try {
                        return await payEquityAuditService.post<CopilotPreview | null>(payload, `v2/${instance}/pay-equity-audit/copilot-preview`, { 'x-subdomain': instanceTopic ?? '' });
                    } catch {
                        throw new Error('Failed to fetch copilot preview.');
                    }

                },
                enabled: () => enabled
            });
        }
    },
    mutations: {
        update: {
            mutationFn: async (payload: UpdateDraftPayload): Promise<void> => {
                const { instanceTopic, userId, role } = useLoginStore();
                const inputValidation = updatePayEquityAuditDraftV2Validator.safeParse(payload.params);
                if (inputValidation.success === false) throw new Error('Invalid input. Failed to update draft.');
                const resgate = ResgateManager.getInstance();
                return resgate.call<void>(`v2.${instanceTopic}.${userId?.toLowerCase()}.${role?.role}.pay-equity-audit-draft.${payload.id}`, 'set', inputValidation.data).then(res => {
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return;
                });
            }
        },
        createDraft: {
            mutationFn: async (input: CreateDraftInput): Promise<PayEquityAuditDraft> => {
                const { instanceTopic, userId, role } = useLoginStore();
                const endpoint = `v2.${instanceTopic}.${userId?.toLowerCase()}.${role?.role}.pay-equity-audit-drafts`;
                const resgate = ResgateManager.getInstance();
                const result = await resgate.call<PayEquityAuditDraft>(endpoint, 'create', input);
                if (result instanceof ResErr) throw new Error(result.message);
                return result;
            }
        },
        deleteDrafts: {
            mutationFn: async (ids: string[]): Promise<void> => {
                if (ids.length === 0) throw new Error('No draft ids provided');
                const { instanceTopic, userId, role } = useLoginStore();
                const endpoint = `v2.${instanceTopic}.${userId?.toLowerCase()}.${role?.role}.pay-equity-audit-drafts`;
                const resgate = ResgateManager.getInstance();
                const result = await resgate.call(endpoint, 'delete', ids);
                if (result instanceof ResErr) throw new Error(result.message);
            }
        },
        publishDraft: {
            mutationFn: async (payload: PublishDraftPayload): Promise<string | null> => {
                const { instance, userId, role } = useLoginStore();
                const combinedPayload: PublishDraftInput = {
                    ...payload.input,
                    jobValuationSalaryTypeLocal: createLocalisation()
                };
                const validation = publishDraftInputValidator.safeParse(combinedPayload);

                if (validation.success === false) throw new Error('Invalid input. Failed to publish draft.');

                try {
                    return await payEquityAuditService.post<string | null>(validation.data, `v2/${instance}/${userId?.toLowerCase()}/${role?.role}/pay-equity-audit-draft/${payload.id}/publish`, { 'x-subdomain': instance ?? '' });
                } catch (error) {
                    console.error(error);
                    throw new Error('Failed to publish draft.');
                }
            }
        }
    }
};
